// ./src/utils/venueCapacity.ts
//
// A VENUE KNOWS HOW MANY PEOPLE FIT IN IT — so choosing one should answer the capacity question
// (owner, 2026-09-26, follow-up to ROADMAP A2b #53).
//
// ⚠⚠ THE HARD PART IS NOT FILLING THE FIELD, IT IS KNOWING WHEN NOT TO. A host who typed 50 into a
// 500-seat hall meant 50, and silently replacing that the next time they touch the venue picker
// would be a data change they never asked for, on a number that decides who gets turned away at the
// door. So this tracks PROVENANCE: a capacity this rule filled in may be replaced by this rule; a
// capacity the host typed is never touched.
//
// ⚠ Pure, and apart from every component that uses it (the Create Event wizard and the event details
// editor both do), so `npm run check-event-defaults` can RUN it. The two surfaces have completely
// different state shapes; the rule must not be written twice.

/** What a caller knows before the change. */
export interface VenueCapacityState {
  /** The capacity currently on the event/draft — null when the host has not limited it. */
  capacity: number | null;
  /**
   * The venue id the CURRENT capacity was taken from, or null when the number is the host's own.
   * ⚠ This is the whole mechanism. Without it there is no way to tell "500 because I picked the big
   * hall" from "500 because I typed it", and the rule has to either clobber the host or never update.
   */
  capacityFromVenueId: string | null;
}

/** The venue just chosen (or cleared). */
export interface VenueCapacitySource {
  id?: string | null;
  capacity?: number | null;
}

/** The patch to apply, or null when nothing should change. */
export interface VenueCapacityPatch {
  capacity: number | null;
  capacityFromVenueId: string | null;
}

/**
 * What the capacity should become when a venue is chosen, changed or cleared.
 *
 * The rules, in order:
 *  1. **The host's own number is never touched.** `capacityFromVenueId === null` with a capacity set
 *     means they typed it.
 *  2. **An empty capacity is filled from the venue** — this is the whole point of the item: picking
 *     the hall answers the question, and "Limit the number of guests" comes on because a limit now
 *     exists. (Both surfaces derive that switch from `capacity != null`, so nothing else is needed.)
 *  3. **A capacity that came from a venue FOLLOWS the venue.** Swapping the 500-seat hall for the
 *     80-seat room updates 500 → 80; leaving it at 500 would be a number nothing in the product
 *     stands behind.
 *  4. **Clearing the venue clears a capacity that came from one**, and only that one.
 *  5. **A venue with no capacity recorded changes nothing** — it has no answer to give, and writing
 *     null over a venue-derived number would silently drop a limit the host is relying on.
 *
 * Returns null when there is nothing to change, so a caller can skip the state update entirely.
 */
export const venueCapacityPatch = (
  state: VenueCapacityState,
  venue: VenueCapacitySource | null | undefined,
): VenueCapacityPatch | null => {
  const hostSetIt = state.capacity != null && !state.capacityFromVenueId;

  if (hostSetIt) {
    return null;
  }

  const venueId = String(venue?.id ?? '').trim() || null;
  const venueCapacity = Number(venue?.capacity);
  const offered = Number.isFinite(venueCapacity) && venueCapacity > 0 ? Math.floor(venueCapacity) : null;

  // The venue was cleared: drop a capacity that came from one, leave anything else alone.
  if (!venueId) {
    if (state.capacityFromVenueId && state.capacity != null) {
      return { capacity: null, capacityFromVenueId: null };
    }

    return null;
  }

  // ⚠ A venue with no capacity recorded is not an instruction to clear one. Most venues have no
  // number on them, so treating "no answer" as "unlimited" would wipe a limit every time a host
  // picked one of them.
  if (offered == null) {
    return null;
  }

  if (state.capacity === offered && state.capacityFromVenueId === venueId) {
    return null;
  }

  return { capacity: offered, capacityFromVenueId: venueId };
};
